import { CommonModule } from '@angular/common';
import { CUSTOM_ELEMENTS_SCHEMA, Component, Signal, WritableSignal, computed, inject, signal } from '@angular/core';
import { Subject, firstValueFrom } from 'rxjs';

import { TranslocoModule } from '@ngneat/transloco';
import { LoadingIndicatorDirective } from 'src/app/common/directives/loading-indicator.directive';
import { SdDialogDestroyAfterCloseDirective } from 'src/app/common/directives/sd-dialog-destroy-component-after-close.directive';
import { SdValueInjectorDirective } from 'src/app/common/directives/sd-value-injector.directive';
import { WritableApiSignal, refetchApiSignal, toWritableApiSignal } from 'src/app/common/util/api-signal';
import { Dialog } from 'src/app/common/util/dialog';
import { RecertificateService, RecertificationInfo, RecertificationRequest } from 'src/openapi-client';

enum RecertificationChange {
  noChange = 'noChange',
  yes = 'yes',
  no = 'no',
}

@Component({
  selector: 'wct-recertification-popup',
  standalone: true,
  imports: [CommonModule, SdValueInjectorDirective, TranslocoModule, LoadingIndicatorDirective, SdDialogDestroyAfterCloseDirective],
  templateUrl: './recertification-popup.component.html',
  styleUrls: ['./recertification-popup.component.scss'],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class RecertificationPopupComponent extends Dialog {
  private readonly recertificateService: RecertificateService = inject(RecertificateService);

  protected recertificationChange = RecertificationChange;

  protected recertifications: WritableApiSignal<RecertificationInfo[]> = toWritableApiSignal(
    this.recertificateService.getRecertifications()
  );

  protected modifications: Map<string, [RecertificationInfo, string]> = new Map();

  protected recertificationChange$: Subject<RecertificationChange> = new Subject();

  protected readonly loading: WritableSignal<boolean> = signal(false);

  protected readonly disabled: Signal<boolean> = computed(() => this.loading() || !this.recertifications());

  protected modify($event: Event, info: RecertificationInfo): void {
    if (!$event.target || !('value' in $event.target)) {
      throw new Error('Invalid event');
    }

    const value: RecertificationChange = $event.target.value as RecertificationChange;

    this.setModification(value, info);
  }

  protected async save(): Promise<void> {
    if (this.disabled()) {
      return;
    }

    this.loading.set(true);

    try {
      const recertificateRequest: RecertificationRequest[] = Array.from(this.modifications.values()).map(
        entry =>
          <RecertificationRequest>{
            certificated: entry[1] === RecertificationChange.yes,
            clientId: entry[0].userInfo.clientId,
            fondsNr: entry[0].fondsNr
          }
      );

      await firstValueFrom(this.recertificateService.recertificateUsers(recertificateRequest));
      await refetchApiSignal(this.recertificateService.getRecertifications(), this.recertifications, false);
      this.reset();
    } finally {
      this.loading.set(false);
    }
  }

  protected recertificateAll(): void {
    this.recertificationChange$.next(RecertificationChange.yes);

    for (const info of this.recertifications() ?? []) {
      if (info.pending) {
        continue;
      }

      this.setModification(RecertificationChange.yes, info);
    }
  }

  protected reset(): void {
    this.recertificationChange$.next(RecertificationChange.noChange);
    this.modifications.clear();
  }

  private setModification(change: RecertificationChange, info: RecertificationInfo): void {
    const modificationsKey = info.userInfo.clientId + "_" + info.fondsNr;
    if (change === RecertificationChange.noChange) {
      this.modifications.delete(modificationsKey);
      return;
    }

    this.modifications.set(modificationsKey, [info, change]);
  }
}
